import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';

import PhoneInput from 'react-phone-input-2';
import { isEmpty as _isEmpty } from 'lodash-es';
import { Link } from 'react-router-dom';

import { editUser } from '../../store/actions/userActions';
import {
  Button,
  Form,
  Input,
  Modal,
  message,
  Divider,
  Typography,
  Radio,
  Space,
  Progress,
  Alert,
} from 'antd';
import RootLayout from '../../layout/Layout';
import Loader from '../../components/Loader/Loader';
import './styles.less';
import { Controller } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import { isPossiblePhoneNumber } from 'react-phone-number-input';

import { SecondaryNavbar } from '../../components/ActionHeader';
import useCountryCode from '../../hooks/useCountryCode';
import { Trans, useTranslation } from 'react-i18next';
import TokenInput from '../Login/TokenInput';
import PhoneInputFormItem from '../../components/PhoneInput';

import {
  ExclamationCircleFilled,
  PlusOutlined,
  RiseOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import { logoutUser } from '../../store/actions/authActions';
import { getMaxResponseCount, getMaxUploadStorage, hasFeature } from '../../utils/restrictions';
import { isValidSubHostName } from '../../utils/helpers';
import { showUpselling } from '../../store/actions/paymentActions';
import { useMediaQuery } from 'react-responsive';
import { deviceWidth } from '../../utils/media-query';

const Account = ({ match, history }) => {
  const [openOTPModal, setOpenOTPModal] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [openDeleteConfirmModal, setOpenDeleteConfirmModal] = useState(false);
  const dispatch = useDispatch();
  const countryCode = useCountryCode();
  const { me, isLoading } = useSelector((state) => state.auth);
  console.log('🚀 ~ Account ~  me:', me);

  const {
    control,
    handleSubmit,
    getValues,
    formState: { isSubmitting },
  } = useForm({
    defaultValues: {
      name: me?.name || '',
      email: me?.email || '',
      whatsappNumber: me?.whatsappNumber || '',
      customDomain: me?.customDomain?.hostname || '',
    },
  });
  const { isLoading: isUserLoading, error } = useSelector((state) => state.user);
  const { t } = useTranslation();

  // state for form domain
  const [formDomain, setFormDomain] = useState(
    hasFeature(me?.plan, 'customDomain') && me?.customDomain?.hostname ? 'custom' : 'default',
  );

  const onSubmit = async (data) => {
    // temporarily disabled otp verification due to meta template block
    // if (data.whatsappNumber && data.whatsappNumber !== me.whatsappNumber) {
    //   await axios.post('/api/users/verify-number', {
    //     whatsappNumber: data.whatsappNumber,
    //   });
    //   return setOpenOTPModal(true);
    // }

    if (formDomain === 'default') {
      data.customDomain = '';
    }
    dispatch(editUser(data))
      .then(() => message.success('Changes saved successfully', 2))
      .catch((error) => message.error(error.message, 3));
  };

  const handleMerge = async () => {
    try {
      const {
        data: { url },
      } = await axios('/auth/merge');
      window.location = url;
    } catch (e) {
      console.log(e);
    }
  };

  const handleOTPVerification = async (token) => {
    setIsVerifying(true);
    try {
      axios.post('/api/users/verify-number/callback', {
        whatsappNumber: getValues('whatsappNumber'),
        token,
      });
      setOpenOTPModal(false);
      setIsVerifying(false);
      const values = getValues();
      dispatch(editUser(values))
        .then(() => message.success('Changes saved successfully', 2))
        .catch((error) => message.error(error.message, 3));
    } catch (e) {
      message.error(e.message, 3);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      setIsVerifying(true);
      await axios.delete('/api/users/me');
      message.success('Account deleted successfully', 2);
      dispatch(logoutUser(history));
    } catch (e) {
      message.error(e.message, 3);
    } finally {
      setIsVerifying(false);
      setOpenDeleteConfirmModal(false);
    }
  };

  const isMobile = useMediaQuery({ maxWidth: deviceWidth.mobile });

  return (
    <RootLayout innerClass="profile-page-layout" showNavbar={!isMobile}>
      <SecondaryNavbar name={t('common.profile')} />
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <section>
            <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
              <Form.Item label={t('common.name')}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => <Input {...field} />}
                />
              </Form.Item>

              <Controller
                name="email"
                control={control}
                rules={{
                  required:
                    me.provider !== 'whatsapp'
                      ? { value: true, message: t('errors.emailRequired') }
                      : false,
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: t('errors.invalidEmail'),
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <Form.Item label={t('common.accountEmail')}>
                    <Input
                      {...field}
                      disabled={me.provider === 'google'}
                      status={error ? 'error' : ''}
                      ref={field.ref}
                    />
                    {error && <Typography.Text type="danger">{error.message}</Typography.Text>}
                  </Form.Item>
                )}
              />

              {me.provider === 'whatsapp' && !me.email && (
                <Form.Item label={t('profile.mergeAccountLabel')}>
                  <div className="merge-desc">{t('profile.mergeAccountDesc')}</div>
                  <Button
                    type="secondary"
                    onClick={handleMerge}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                  >
                    <img src="/google/g-logo.png" alt="google-logo" height="18" width="18" />
                    <span>{t('profile.mergeAccountButtonText')}</span>
                  </Button>
                </Form.Item>
              )}
              <Controller
                name="whatsappNumber"
                control={control}
                rules={{
                  validate: (value) =>
                    isPossiblePhoneNumber(`+${value}`) || t('formSettings.enterValidNumber'),
                }}
                render={({ field, fieldState: { error } }) => (
                  <Form.Item label={t('common.whatsappNumber')} style={{ marginBottom: '0.5rem' }}>
                    <PhoneInputFormItem {...field} error={error} />
                  </Form.Item>
                )}
              />

              <Controller
                name="customDomain"
                control={control}
                rules={{
                  pattern: {
                    value: /^(?!:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z0-9-]{2,63}\.[a-zA-Z0-9-]{2,63}$/i,
                    message: t('formSettings.domainError'),
                  },
                }}
                style={{ marginBottom: '0.5rem' }}
                render={({ field, fieldState: { error } }) => (
                  <Form.Item
                    label={t('profile.form-domain')}
                    style={{ marginBottom: '1rem' }}
                    help={
                      error ? (
                        error.message
                      ) : (
                        <span>
                          {formDomain === 'default' ? (
                            <span>
                              {t('profile.all-your-form-links-will')}{' '}
                              <b>
                                {formDomain === 'default'
                                  ? 'https://whatsform.com'
                                  : 'https://forms.company.com'}
                              </b>
                              /formname
                            </span>
                          ) : (
                            t('formSettings.customDomainHelp')
                          )}{' '}
                          <a
                            href="https://whatsform.help.center/article/1079-set-up-custom-domain-for-whats-form"
                            target="_blank"
                            rel="noreferrer"
                          >
                            {t('formSettings.customDomainLink')}
                          </a>
                        </span>
                      )
                    }
                    validateStatus={error ? 'error' : ''}
                  >
                    <Radio.Group
                      value={formDomain}
                      onChange={(e) => {
                        if (!hasFeature(me.plan, 'customDomain') && e.target.value === 'custom') {
                          return dispatch(showUpselling('domain'));
                        } else {
                          return setFormDomain(e.target.value);
                        }
                      }}
                      style={{ marginBottom: '0.5rem' }}
                    >
                      <Space direction="vertical">
                        <Radio value="default">https://whatsform.com</Radio>
                        <Radio value="custom">{t('formSettings.customDomainLabel')}</Radio>
                      </Space>
                    </Radio.Group>

                    {formDomain === 'custom' && (
                      <Input
                        {...field}
                        addonBefore="https://"
                        onChange={
                          !hasFeature(me.plan, 'customDomain')
                            ? () => {
                                return dispatch(showUpselling('domain'));
                              }
                            : field.onChange
                        }
                        placeholder="forms.company.com"
                        status={error ? 'error' : ''}
                      />
                    )}
                  </Form.Item>
                )}
              />

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="medium"
                  disabled={isUserLoading}
                  loading={isUserLoading || isSubmitting}
                >
                  {t('common.save')}
                </Button>
              </Form.Item>
            </Form>
            <OTPVerificationModal
              open={openOTPModal}
              handleOk={handleOTPVerification}
              handleCancel={() => setOpenOTPModal(false)}
              isVerifying={isVerifying}
            />
          </section>
          {me?.plan !== 'free' && (
            <>
              <section style={{ padding: '0 20px' }}>
                <Divider style={{ marginTop: 0, marginBottom: '1rem' }} />
              </section>
              <section className="usage-details">
                <UsageSection />
              </section>
            </>
          )}
          <section style={{ padding: '0 20px' }}>
            <Divider />
          </section>

          <section>
            <div className="danger-zone">
              <Typography.Paragraph strong style={{ fontSize: '16px', marginBottom: '0' }}>
                {t('profile.deleteAccount')}
              </Typography.Paragraph>
              <Typography.Paragraph style={{ marginBottom: '12px' }}>
                {t('profile.deleteAccountDesc')}
              </Typography.Paragraph>
              {me?.plan !== 'free' && (
                <Alert
                  style={{ marginBottom: '24px' }}
                  type="warning"
                  description={t('profile.downgradeAccountInstruction')}
                />
              )}

              <Button
                disabled={me?.plan !== 'free'}
                onClick={() => setOpenDeleteConfirmModal(true)}
              >
                {t('profile.deleteAccount')}
              </Button>
            </div>
            <DeleteConfirmModal
              open={openDeleteConfirmModal}
              handleOk={handleDeleteConfirm}
              handleCancel={() => setOpenDeleteConfirmModal(false)}
              isVerifying={isVerifying}
            />
          </section>
        </>
      )}
    </RootLayout>
  );
};
const tokenRegex = /^[0-9]{6}$/;

const UsageSection = () => {
  const { t } = useTranslation();
  const { me } = useSelector((state) => state.auth);
  const { plan, totalResponseCount, totalUploadSize } = me || {};
  const maxUploadStorage = getMaxUploadStorage(plan);
  const maxResponseCount = getMaxResponseCount(plan);

  const displayUploadSize = useMemo(() => {
    // if size less that 1GB, return in MB
    if (totalUploadSize < 1000) {
      return totalUploadSize.toFixed(2) + ' MB';
    }
    // if size is more than 1GB, return in GB
    return (totalUploadSize / 1000).toFixed(2);
  }, [totalUploadSize]);

  const uploadSizePercent = (totalUploadSize / 1000 / Math.round(maxUploadStorage / 1000)) * 100;
  const responseCountPercent = (totalResponseCount / maxResponseCount) * 100;

  return (
    <>
      <div className="flex" style={{ marginBottom: '1rem' }}>
        <Typography.Text strong>{t('billing.current-plan-expiry', 'Usage')}</Typography.Text>
        <Link to="/billing">
          <Button type="default" icon={<RiseOutlined />}>
            {t('common.upgrade', 'Increase storage')}
          </Button>
        </Link>
      </div>

      <div style={{ marginTop: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography.Text>{t('billing.upload-storage', 'Upload Storage')} </Typography.Text>
          <Typography.Text>
            {displayUploadSize} / {Math.round(maxUploadStorage / 1000)}GB
          </Typography.Text>
        </div>
        <Progress
          percent={uploadSizePercent}
          showInfo={false}
          status={uploadSizePercent > 90 ? 'exception' : 'success'}
        />
      </div>
      <div style={{ marginTop: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography.Text>
            {t('billing.response-storage', 'Total Response Storage')}{' '}
          </Typography.Text>
          <Typography.Text>
            {totalResponseCount} / {maxResponseCount}
          </Typography.Text>
        </div>
        <Progress
          percent={responseCountPercent}
          showInfo={false}
          status={responseCountPercent > 90 ? 'exception' : 'success'}
        />
      </div>
    </>
  );
};

const OTPVerificationModal = ({ open, handleOk, handleCancel, isVerifying }) => {
  const [tokenInputs, setTokenInputs] = useState(['', '', '', '', '', '']);
  const { t } = useTranslation();
  return (
    <Modal
      visible={open}
      width={400}
      okText={t('common.verify')}
      cancelText={t('common.cancel')}
      // okType="success"
      onOk={() => handleOk(tokenInputs.join(''))}
      centered
      confirmLoading={isVerifying}
      onCancel={handleCancel}
    >
      <div className="token-input-container">
        <div className="token-input-section">
          <label style={{ color: '#222222aa', fontWeight: '600' }}>
            {t('profile.enterOTP')}{' '}
            <span style={{ color: '#2AB200' }}>
              WhatsApp <WhatsAppOutlined />
            </span>
          </label>
          <TokenInput
            tokenInputs={tokenInputs}
            setTokenInputs={setTokenInputs}
            isLoading={isVerifying}
            tokenRegex={tokenRegex}
          />
        </div>
      </div>
    </Modal>
  );
};
const DeleteConfirmModal = ({ open, handleOk, handleCancel, isVerifying }) => {
  const [input, setInput] = useState('');
  const { t } = useTranslation();

  return (
    <Modal
      visible={open}
      width={600}
      okText={t('profile.deleteAccountPermanently')}
      cancelText={t('common.cancel')}
      okType="danger"
      okButtonProps={{
        type: 'primary',
        disabled: input !== t('profile.deleteAccount'),
        loading: isVerifying,
      }}
      onOk={() => handleOk()}
      centered
      title={
        <>
          <ExclamationCircleFilled style={{ color: 'red', marginRight: '0.5rem' }} />
          {t('profile.deleteAccountPermanently')}
        </>
      }
      // confirmLoading={isVerifying}
      onCancel={handleCancel}
    >
      <Typography.Paragraph>{t('profile.deleteAccountWarning')}</Typography.Paragraph>
      <Typography.Paragraph>
        <ul>
          <li>User data</li>
          <li>Froms</li>
          <li>Responses</li>
        </ul>
      </Typography.Paragraph>
      <Typography.Paragraph>
        <Trans i18nKey="profile.confirmationMessage"></Trans>
      </Typography.Paragraph>
      <Input
        value={input}
        onChange={(e) => setInput(e.target.value)}
        autoFocus
        onPaste={(e) => e.preventDefault()}
      />
    </Modal>
  );
};

export default Account;
